import React from 'react';
import { Box, Button, Typography } from '@heycater/design-system';
import { BoxProps, TypographyProps } from '@material-ui/core';
import Image, { StaticImageData } from 'next/image';
import { Trans, useTranslation } from 'react-i18next';
import styled, { useTheme } from 'styled-components';

import step1Img from '@images/food-program-page/steps/step1.png';
import step2Img from '@images/food-program-page/steps/step2.png';
import step3Img from '@images/food-program-page/steps/step3.png';
import step4Img from '@images/food-program-page/steps/step4.png';
import { useFoodProgramCta } from 'static_pages/foodProgram/useFoodProgramCta';
import { FoodProgramH2 } from 'static_pages/foodProgram/Utils';
import Container from 'system/components/Container';

const STEPS = [
  {
    titleKey: 'steps.1.title',
    descriptionKey: 'steps.1.description',
    image: step1Img,
  },
  {
    titleKey: 'steps.2.title',
    descriptionKey: 'steps.2.description',
    image: step2Img,
  },
  {
    titleKey: 'steps.3.title',
    descriptionKey: 'steps.3.description',
    image: step3Img,
  },
  {
    titleKey: 'steps.4.title',
    descriptionKey: 'steps.4.description',
    image: step4Img,
  },
];

const FoodProgramSteps = (props: BoxProps) => {
  const theme = useTheme();
  const { t } = useTranslation('food_program');
  const ctaHref = useFoodProgramCta();

  return (
    <Box as="section" textAlign="center" {...props}>
      <Container size="sm">
        <FoodProgramH2 mb={6}>
          <Trans
            t={t}
            i18nKey="steps.title"
            components={[
              <Box key="1" as="span" color={theme.palette.secondary[400]} />,
            ]}
          />
        </FoodProgramH2>
      </Container>
      <Container size="md">
        <Steps>
          {STEPS.map(({ titleKey, descriptionKey, image }) => {
            return (
              <Step key={titleKey}>
                <StepImage src={image} />

                <StepTitle>
                  <Trans
                    t={t}
                    i18nKey={titleKey}
                    components={{ '1': <br /> }}
                  />
                </StepTitle>

                <Typography fontSize={16}>{t(descriptionKey)}</Typography>
              </Step>
            );
          })}
        </Steps>
        <Box mt={6}>
          <Button component="a" rounded href={ctaHref} target="_blank">
            {t('cta')}
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

const Steps = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.spacing(3)}px;

  align-items: start;
  justify-items: center;

  ${({ theme }) => theme.breakpoints.up('sm')} {
    grid-template-columns: 1fr 1fr;
  }

  ${({ theme }) => theme.breakpoints.up('md')} {
    grid-template-columns: 1fr 1fr 1fr 1fr;
    gap: ${({ theme }) => theme.spacing(4)}px;
  }

  ${({ theme }) => theme.breakpoints.up('lg')} {
    gap: ${({ theme }) => theme.spacing(9)}px;
  }
`;

const Step = styled.div`
  flex-grow: 1;
  flex-shrink: 0;
  width: 200px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)}px;
`;

const StepImage = (props: { src: string | StaticImageData }) => (
  <Image src={props.src} width={160} height={158} objectFit="contain" alt="" />
);

const StepTitle = (props: TypographyProps) => (
  <Typography
    lineHeight={{ xs: 1.5, sm: 1.5 }}
    fontSize={20}
    component="h3"
    fontWeight={600}
    {...props}
  />
);

export default FoodProgramSteps;
